import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Plot from 'react-plotly.js';
import { ManRadioButtonGroup, ManRadioButton, ManText } from '@craft-design-system/core-components-react';
import { Popover, OverlayTrigger } from 'react-bootstrap';
import { useFilterContext } from '../FilterContext';
import useGraphRadio from '../../utils/useGraphRadio';
import { useResetContext } from '../ResetContext';
import useSegmentedChartQuery from '../../utils/useSegmentedChartQuery';
import AverageDisplay from '../ride_analysis_kpi/Average_value';
import DownloadButtonComponent from '../DownloadButtonComponent';
import Shimmer_graph from '../shimmer_graph';
import ifav from './ifav.png';
import Zoom from '../Zoom';
import SegmentedControl from '../ride_analysis_kpi/SegmentedControl';
import AIToggleButton from '../AIToggleButton';

const safeJsonParse = (value) => {
  try {
    if (typeof value === "string") return JSON.parse(value);
    return value;
  } catch {
    return [];
  }
};

const sanitizeNumbers = (obj) => {
  if (Array.isArray(obj)) return obj.map(sanitizeNumbers);
  if (obj && typeof obj === "object") {
    const cleaned = {};
    Object.keys(obj).forEach(k => cleaned[k] = sanitizeNumbers(obj[k]));
    return cleaned;
  }
  if (typeof obj === "number") return Number.isFinite(obj) ? obj : null;
  return obj;
};

const Battery_temp_fast = ({ value }) => {
  const [chartData, setChartData] = useState(null);
  const [downloadData, setDownloadData] = useState([]);
  const [avgValue, setAvgValue] = useState(null);
  const [radioButtons, setRadioButtons] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [chartType, setChartType] = useState("Bar");
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const radio_query = useGraphRadio();
  const { filterdata } = useFilterContext();
  const { startDate, endDate } = filterdata || {};
  const { resetdata } = useResetContext();
  const { fetchSegmentedChart } = useSegmentedChartQuery();

  // 🔥 Debug logging to see what resetdata/category is
  useEffect(() => {
    console.log('🔍 resetdata (category):', resetdata);
    console.log('🔍 filterdata:', filterdata);
  }, [resetdata, filterdata]);

  const handleSelectChange1 = (event) => {
    console.log('📻 Radio button changed:', event.detail.name, event.detail.value);
    console.log('📻 Current resetdata:', resetdata);
    
    setRadioButtons({
      ...radioButtons,
      [event.detail.name]: event.detail.value,
    });
  };

  /* 🔥 SEGMENTED CHART FETCH */
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        console.log('📊 Fetching segmented chart with category:', resetdata);
        
        const res = await fetchSegmentedChart({
          vinShort: value.vin_short,
          chartType,
          category: resetdata, // This should contain the category
          columnname: 'Battery temp fast',
        });

        const chartObj = safeJsonParse(res.chart);

        setChartData(chartObj);
        setDownloadData(res.downloadData || []);
        setAvgValue(res.average?.average ?? res.average ?? null);

      } catch (err) {
        console.error("Segmented chart fetch error:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (!isInitialLoad && chartType && value?.vin_short) {
      fetchData();
    } else {
      setIsInitialLoad(false);
    }
  }, [chartType, resetdata]); // 🔥 Added resetdata as dependency

  /* 🔥 RADIO FETCH - Enhanced to include category */
  useEffect(() => {
    if (!radioButtons) return;

    console.log('📻 Radio fetch triggered with:', radioButtons);
    console.log('📻 Current category (resetdata):', resetdata);

    setIsLoading(true);

    // 🔥 Enhanced params to include category properly
    const params = {
      vins_short: [value.vin_short],
      radio_value: radioButtons,
      columnname: 'Battery temp fast',
      startDate,
      endDate,
      filterdata: {
        ...filterdata,
        category: resetdata, // Ensure category is included
        resetdata: resetdata // Also include as resetdata for backward compatibility
      },
      resetdata: resetdata, // 🔥 CRITICAL: Pass category as resetdata
      category: resetdata   // 🔥 CRITICAL: Also pass as category
    };

    console.log('📻 Sending radio query with params:', params);

    radio_query(params)
      .then(res => {
        console.log('📻 Radio query response:', res);

        // 🔥 Support BOTH response formats
        const payload = res.jsonData ?? res;

        const chartObj = safeJsonParse(payload.chart);
        const download = payload.downloadData ?? [];
        const avg = payload.average;

        setChartData(chartObj);
        setDownloadData(download);
        setAvgValue(avg?.average ?? avg ?? null);
      })
      .catch(err => {
        console.error("❌ Radio fetch failed:", err);
      })
      .finally(() => setIsLoading(false));

  }, [radioButtons, resetdata]); // 🔥 Added resetdata as dependency

  /* 🔥 INITIAL LOAD FROM PROPS */
  useEffect(() => {
    console.log('🔍 Initial load, value prop:', value);
    
    const chartObj = safeJsonParse(value?.battery_temp_during_fast_charging?.chart);

    setChartData(chartObj);
    setDownloadData(value?.battery_temp_during_fast_charging?.downloadData || []);

    const avg = value?.battery_temp_during_fast_charging?.average;
    setAvgValue(avg?.average ?? avg ?? null);

  }, [value]);

  // 🔥 Monitor resetdata changes
  useEffect(() => {
    console.log('🔄 resetdata changed to:', resetdata);
    
    // If we have radio buttons selected and resetdata changes, refetch
    if (radioButtons && resetdata) {
      console.log('🔄 Refetching due to category change');
      // Trigger refetch by updating radioButtons state slightly
      setRadioButtons({...radioButtons});
    }
  }, [resetdata]);

  const cleanedAIData = sanitizeNumbers(downloadData);

  const popover = (
    <Popover id="popover-basic">
      <Popover.Body>
        <strong>Definition:</strong><br />
        The battery temperature (system) during fast charging cycle is displayed using minimum, average and maximum values.<br /><br />
  
        <strong>Telematic raw data for this KPI:</strong><br />
        TBM3: <br />
        • batterytemperature<br />
        CM4: 
        • batterytemperature<br /><br />
  
        <strong>Time series data:</strong><br />
        If multiple vehicles are selected, average values are displayed. Days without data are not taken into account for the calculation.<br /><br />
  
        <strong>Histogram:</strong><br />
        Each bar represents the number of cycles that fall within a certain range (bin) of <strong>average battery system temperature values during fast charging.</strong><br />
        All average battery system temperature values during fast charging since start of data acquisition for the selected vehicle(s) are visualized per default.<br /><br />
  
        <strong>Boxplot:</strong><br />
        The boxplot shows the <strong>distribution of average battery system temperature values during fast charging</strong> for the selected vehicle(s) since start of data acquisition.<br />
        If multiple vehicles are selected, the boxplot reflects the combined distribution of all their values.<br />
        • The <strong>box</strong> represents the middle 50% of values (from the 25th percentile ("q1") to the 75th percentile ("q3")).<br />
        • The line <strong>inside the box</strong> shows the median (50th percentile).<br />
        • The whiskers extend to show the range of typical values ("min" and "max") and visualize <strong>the spread of the data</strong> beyond the central box.
      </Popover.Body>
    </Popover>
  );

  return (
    <div style={{ padding: 16 }}>
      {/* Debug info - remove in production */}
      {/* <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
        Debug: Category = {resetdata || 'None'}, Radio = {JSON.stringify(radioButtons)}
      </div> */}

       <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, alignItems: 'center' }}>
        <div style={{ flex: 1, textAlign: 'center', marginLeft: '80px' }}>
          <ManText
            className="card-title-text"
            variant="component-l"
            as="span"
            size="l"
            style={{ lineHeight: 'normal' }}
          >
            Battery temperature (system) during fast charging
          </ManText>
        </div>

        <div style={{ 
          display: 'flex', 
          gap: 10, 
          flexDirection: "row-reverse",
          alignItems: 'center' // 🔥 Added to align all icons in the center
        }}>
          <OverlayTrigger trigger="hover" placement="left" overlay={popover}>
            <img 
              className="man-logovv1" 
              src={ifav} 
              alt="info" 
              style={{ 
                cursor: 'pointer',
                alignSelf: 'center' // 🔥 Ensures info icon aligns with other icons
              }} 
            />
          </OverlayTrigger>

          <Zoom
            chartData={chartData?.data}
            chartLayout={chartData?.layout}
            chartTitle="Battery temperature (system) during fast charging"
          />

          <DownloadButtonComponent
            data={downloadData}
            fileName={`battery_temp_fast_${chartType.toLowerCase()}_${radioButtons?.['Battery Temperature Fast Charge']?.toLowerCase() || 'daily'}.xlsx`}
          />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
        {chartType === 'Bar'
          ? <div style={{ flexShrink: 0 }}><AverageDisplay averages={avgValue} unit="°C" /></div>
          : <div style={{ width: 150 }} />
        }

        {chartType === 'Bar' && (
          <div style={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
            <ManRadioButtonGroup 
              name="Battery Temperature Fast Charge" 
              invalid="false"
              onManChange={handleSelectChange1}
            >
              <ManRadioButton name="Battery Temperature Fast Charge" label="Daily" value="Daily" checked />
              <ManRadioButton name="Battery Temperature Fast Charge" label="Monthly" value="Monthly" />
              <ManRadioButton name="Battery Temperature Fast Charge" label="Yearly" value="Yearly" />
            </ManRadioButtonGroup>
          </div>
        )}

        <div style={{ marginLeft: 'auto' }}>
          <SegmentedControl
            selected={chartType}
            onChange={setChartType}
            options={["Bar", "Histogram", "Boxplot"]}
          />
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
  {isLoading ? (
    <Shimmer_graph />
  ) : (
    chartData && (
      <>
        <Plot
          data={chartData.data}
          layout={{
            ...chartData.layout,
            autosize: true,
            responsive: true,
            margin: chartType === 'Bar' 
              ? { t: 10, r: 10, l: 10, b: 160 } // 🔥 Reduced margin since button is moved outside
              : { t: 10, r: 10, l: 10, b: 180 },
          }}
          useResizeHandler={true}
        />

        {/* 🔥 AI Toggle Button - Bottom left corner */}
        {chartType === 'Bar' && cleanedAIData?.length > 0 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-start', // 🔥 Left alignment
            marginTop: '16px' // 🔥 Space from chart
          }}>
            <AIToggleButton
              downloadData={cleanedAIData}
              radioButtonState={radioButtons}
              kpiName="Battery Temperature Fast Charge"
              chartType={chartType}
              vinShort={value.vin_short}
              title="AI | Automotive Intelligence - Fast Charging Battery Temperature Analysis"
            />
          </div>
        )}
      </>
    )
  )}
</div>
    </div>
  );
};

Battery_temp_fast.propTypes = {
  value: PropTypes.shape({
    vin_short: PropTypes.string.isRequired,
    battery_temp_during_fast_charging: PropTypes.array.isRequired,
  }).isRequired,
};

export default Battery_temp_fast;